(() => {
  if (window.__seednaryProductCardLoaded) return;
  window.__seednaryProductCardLoaded = true;

  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents);
    }
    return new Intl.NumberFormat(document.documentElement.lang || "en", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "GBP",
    }).format(cents / 100);
  };

  const rootPath = () => (window.Shopify?.routes?.root || "/").replace(/\/$/, "");
  const cartAddUrl = () => `${rootPath()}/cart/add.js`;
  const cartUrl = () => `${rootPath()}/cart.js`;

  const getCartSectionIds = () => {
    return [...document.querySelectorAll("cart-items-component, [data-seednary-cart-section-id]")]
      .map((node) => node.dataset.sectionId || node.dataset.seednaryCartSectionId)
      .filter(Boolean);
  };

  const appendCartSections = (formData) => {
    const sectionIds = getCartSectionIds();
    if (sectionIds.length) {
      formData.set("sections", sectionIds.join(","));
      formData.set("sections_url", window.location.pathname);
    }
    return sectionIds;
  };

  const dispatchCartEvents = (cart, sourceId, itemCount, sections) => {
    const detail = {
      resource: cart,
      sourceId,
      data: {
        source: "product-form-component",
        itemCount,
        sections,
      },
    };
    document.dispatchEvent(new CustomEvent("cart:update", { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true, detail }));
    window.dispatchEvent(new CustomEvent("cart:update", { detail }));
  };

  const updateCartCount = (cart) => {
    const count = cart?.item_count ?? 0;
    document.querySelectorAll("[data-cart-count], .cart-count, .cart-bubble__text-count, .cart-count-bubble span:first-child, .cart-count-bubble span, [data-header-cart-count]").forEach((node) => {
      node.textContent = count;
      node.hidden = count === 0 && node.hasAttribute("hidden");
      node.classList.toggle("hidden", count === 0);
    });
  };

  const openCartDrawer = () => {
    const seednaryDrawer = document.querySelector("[data-seednary-cart-drawer]");
    if (seednaryDrawer) {
      document.dispatchEvent(new CustomEvent("seednary:cart:open", { bubbles: true }));
      return;
    }

    const trigger = document.querySelector('[data-testid="cart-drawer-trigger"], [aria-controls="CartDrawer"], [data-cart-drawer-toggle]');
    if (trigger instanceof HTMLElement) {
      trigger.click();
      return;
    }

    const drawer = document.querySelector("cart-drawer-component, cart-drawer, #CartDrawer, .cart-drawer");
    if (drawer) {
      if (typeof drawer.open === "function") {
        drawer.open();
        return;
      }
      if (typeof drawer.showDialog === "function") {
        drawer.showDialog();
        return;
      }
      const innerDialog = drawer.querySelector("dialog") || drawer.refs?.dialog;
      if (innerDialog && typeof innerDialog.showModal === "function" && !innerDialog.open) {
        innerDialog.showModal();
        return;
      }
      drawer.setAttribute("open", "");
      drawer.classList.add("is-open", "active");
      drawer.dispatchEvent(new CustomEvent("cart-drawer:open", { bubbles: true }));
      return;
    }

    const fallbackTrigger = document.querySelector("cart-icon a, .header-actions__cart-icon a, .header-actions__cart-icon");
    if (fallbackTrigger instanceof HTMLElement) {
      fallbackTrigger.click();
    }
  };

  const submitCartFormData = async (formData, submitter) => {
    appendCartSections(formData);
    const variantId = formData.get("id")?.toString() || submitter?.dataset.variantId || "";
    const quantity = Number(formData.get("quantity") || 1);
    const response = await fetch(cartAddUrl(), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: formData,
    });

    const added = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw { message: added.description || added.message || "Unable to add item", sourceId: variantId, response: added };
    }

    const cart = await fetch(cartUrl(), { headers: { Accept: "application/json" } }).then((res) => res.json());
    updateCartCount(cart);
    dispatchCartEvents(cart, variantId, quantity, added.sections);
    window.setTimeout(openCartDrawer, 0);
    return cart;
  };

  const addFormToCart = async (form, submitter) => {
    return submitCartFormData(new FormData(form), submitter);
  };

  const addVariantToCart = async (variantId, quantity = 1) => {
    const formData = new FormData();
    formData.set("id", variantId);
    formData.set("quantity", quantity.toString());
    return submitCartFormData(formData, { dataset: { variantId } });
  };

  const getProductData = (card) => {
    const script = card.querySelector("[data-seednary-product-json]");
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  };

  const getVariantImage = (product, variant) => {
    const imageId = variant?.featured_media?.id || variant?.featured_image?.id;
    if (imageId && Array.isArray(product.media)) {
      const media = product.media.find((item) => item.id === imageId);
      if (media?.preview_image?.src) return media.preview_image.src;
      if (media?.src) return media.src;
    }
    if (variant?.featured_image?.src) return variant.featured_image.src;
    if (product.featured_image) return product.featured_image;
    return "";
  };

  const findVariant = (product, selected) => {
    return product.variants.find((variant) => {
      return product.options.every((optionName, index) => {
        return variant[`option${index + 1}`] === selected[optionName];
      });
    });
  };

  const ensureModal = () => {
    let modal = document.querySelector("[data-seednary-variant-modal]");
    if (modal) return modal;

    modal = document.createElement("div");
    modal.className = "seednary-variant-modal";
    modal.hidden = true;
    modal.dataset.seednaryVariantModal = "";
    modal.innerHTML = `
      <div class="seednary-variant-modal__dialog" role="dialog" aria-modal="true">
        <div class="seednary-variant-modal__head">
          <strong data-modal-title></strong>
          <button class="seednary-variant-modal__close" type="button" data-modal-close aria-label="Close">×</button>
        </div>
        <div class="seednary-variant-modal__body">
          <div class="seednary-variant-modal__summary">
            <img data-modal-image alt="">
            <div>
              <div class="seednary-variant-modal__price" data-modal-price></div>
              <div class="seednary-variant-modal__status" data-modal-status></div>
            </div>
          </div>
          <div data-modal-options></div>
          <button class="seednary-variant-modal__submit" type="button" data-modal-submit>Add to Cart</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    modal.addEventListener("click", (event) => {
      if (event.target === modal || event.target.closest("[data-modal-close]")) {
        modal.hidden = true;
      }
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") modal.hidden = true;
    });
    return modal;
  };

  const openVariantModal = (product) => {
    const modal = ensureModal();
    const title = modal.querySelector("[data-modal-title]");
    const image = modal.querySelector("[data-modal-image]");
    const price = modal.querySelector("[data-modal-price]");
    const status = modal.querySelector("[data-modal-status]");
    const optionsRoot = modal.querySelector("[data-modal-options]");
    const submit = modal.querySelector("[data-modal-submit]");
    const selected = {};

    product.options.forEach((optionName, index) => {
      const firstAvailable = product.variants.find((variant) => variant.available)?.[`option${index + 1}`];
      selected[optionName] = firstAvailable || product.variants[0]?.[`option${index + 1}`];
    });

    const renderSelected = () => {
      const variant = findVariant(product, selected);
      if (variant) {
        image.src = getVariantImage(product, variant);
        image.alt = product.title;
        price.innerHTML = variant.compare_at_price && variant.compare_at_price > variant.price
          ? `<s>${formatMoney(variant.compare_at_price)}</s><strong>${formatMoney(variant.price)}</strong>`
          : `<span>${formatMoney(variant.price)}</span>`;
        status.textContent = variant.available ? "In stock" : "Sold out";
        submit.disabled = !variant.available;
        submit.dataset.variantId = variant.id;
      } else {
        status.textContent = "This combination is unavailable";
        submit.disabled = true;
        submit.dataset.variantId = "";
      }

      optionsRoot.querySelectorAll("[data-option-value]").forEach((button) => {
        const optionName = button.dataset.optionName;
        button.classList.toggle("is-active", selected[optionName] === button.dataset.optionValue);
      });
    };

    title.textContent = product.title;
    optionsRoot.innerHTML = "";
    product.options.forEach((optionName, index) => {
      const values = [...new Set(product.variants.map((variant) => variant[`option${index + 1}`]).filter(Boolean))];
      const fieldset = document.createElement("fieldset");
      fieldset.className = "seednary-variant-modal__option";
      fieldset.innerHTML = `<legend>${optionName}</legend><div class="seednary-variant-modal__values"></div>`;
      const valuesRoot = fieldset.querySelector(".seednary-variant-modal__values");
      values.forEach((value) => {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = value;
        button.dataset.optionName = optionName;
        button.dataset.optionValue = value;
        button.dataset.optionValueButton = "";
        button.addEventListener("click", () => {
          selected[optionName] = value;
          renderSelected();
        });
        valuesRoot.appendChild(button);
      });
      optionsRoot.appendChild(fieldset);
    });

    submit.onclick = async () => {
      if (!submit.dataset.variantId) return;
      submit.disabled = true;
      submit.setAttribute("aria-busy", "true");
      try {
        await addVariantToCart(submit.dataset.variantId, 1);
        modal.hidden = true;
      } catch (error) {
        status.textContent = error.message;
        document.dispatchEvent(new CustomEvent("cart:error", {
          bubbles: true,
          detail: { sourceId: error.sourceId || submit.dataset.variantId, data: error.response || error },
        }));
      } finally {
        submit.disabled = false;
        submit.removeAttribute("aria-busy");
      }
    };

    renderSelected();
    modal.hidden = false;
  };

  document.addEventListener("submit", async (event) => {
    const form = event.target.closest?.("[data-seednary-card-form]");
    if (!form) return;
    event.preventDefault();

    const trigger = form.querySelector("[data-seednary-add-trigger]");
    if (!trigger || trigger.disabled) return;

    const card = trigger.closest("[data-seednary-product-card]");
    const product = getProductData(card);
    if (!product) return;

    const tags = (product.tags || []).map((tag) => String(tag).toLowerCase());
    const isPreorder = tags.some((tag) => tag.includes("preorder") || tag.includes("pre-order") || tag.includes("预购"));
    if (isPreorder) {
      window.location.href = trigger.dataset.productUrl || card.dataset.productUrl || product.url;
      return;
    }

    if (!trigger.dataset.variantId) {
      openVariantModal(product);
      return;
    }

    trigger.disabled = true;
    trigger.setAttribute("aria-busy", "true");
    try {
      await addFormToCart(form, trigger);
    } catch (error) {
      console.error(error);
      document.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: { sourceId: error.sourceId || trigger.dataset.variantId, data: error.response || error },
      }));
    } finally {
      trigger.disabled = false;
      trigger.removeAttribute("aria-busy");
    }
  });

  document.addEventListener("click", async (event) => {
    const carouselButton = event.target.closest("[data-seednary-carousel-button]");
    if (carouselButton) {
      const section = carouselButton.closest("[data-seednary-featured-collection]");
      const track = section?.querySelector("[data-seednary-carousel-track]");
      if (track) {
        const direction = carouselButton.dataset.seednaryCarouselButton === "next" ? 1 : -1;
        track.scrollBy({ left: direction * track.clientWidth, behavior: "smooth" });
      }
      return;
    }

    const trigger = event.target.closest("[data-seednary-add-trigger]");
    if (!trigger) return;
    event.preventDefault();

    const card = trigger.closest("[data-seednary-product-card]");
    const product = getProductData(card);
    if (!product) return;

    const tags = (product.tags || []).map((tag) => String(tag).toLowerCase());
    const isPreorder = tags.some((tag) => tag.includes("preorder") || tag.includes("pre-order") || tag.includes("预购"));
    if (isPreorder) {
      window.location.href = trigger.dataset.productUrl || card.dataset.productUrl || product.url;
      return;
    }

    if (trigger.dataset.variantId) {
      const form = trigger.closest("form");
      if (!form) return;
      trigger.disabled = true;
      trigger.setAttribute("aria-busy", "true");
      try {
        await addFormToCart(form, trigger);
      } catch (error) {
        console.error(error);
        document.dispatchEvent(new CustomEvent("cart:error", {
          bubbles: true,
          detail: { sourceId: error.sourceId || trigger.dataset.variantId, data: error.response || error },
        }));
      } finally {
        trigger.disabled = false;
        trigger.removeAttribute("aria-busy");
      }
      return;
    }

    openVariantModal(product);
  });
})();
